"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { motion } from "framer-motion";
import Image from "next/image";
import { HiStar, HiUsers, HiHeart, HiClock } from "react-icons/hi";
import PageHero from "@/components/sections/PageHero";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCards } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cards";
import { STORY_IMAGES, getTeamMembers } from "@/data/about";
import { useLanguage } from "@/lib/i18n";

export default function AboutClient() {
  const { language } = useLanguage();
  const teamMembers = getTeamMembers(language);

  const pageContent = language === "id" ? {
    title: "Tentang KinKen Kost Elite",
    subtitle: "Bukan sekadar kost biasa. KinKen menghadirkan standar baru hunian premium di Bali, di mana setiap detail dirancang untuk mendukung gaya hidup urban yang canggih.",
    superText: "Our Philosophy",
    storyTitle: "Redefining Luxury Living,",
    storyHighlight: "Crafting Experiences.",
    storyP1: "KinKen Kost Elite lahir dari visi menghadirkan konsep hunian yang melampaui ekspektasi. Di jantung Renon, Denpasar Selatan, kami menjawab kebutuhan profesional modern dan ekspatriat yang menginginkan lebih dari sekadar tempat tinggal.",
    storyP2: "Setiap kamar di KinKen dirancang seperti apartemen pribadi mini dengan sentuhan kemewahan Bali. Dari layanan concierge 24/7 hingga fasilitas setara hotel bintang 5, kami menciptakan",
    sanctuary: "sanctuary",
    storyP2End: "di mana produktivitas bertemu dengan kenyamanan tertinggi.",
    occupancyLabel: "Occupancy Rate",
    satisfactionLabel: "Resident Satisfaction",
    values: [
      { icon: HiStar, title: "Elite Standard", desc: "Kualitas premium dalam setiap aspek hunian." },
      { icon: HiUsers, title: "Curated Community", desc: "Komunitas profesional dan ekspatriat terpilih." },
      { icon: HiHeart, title: "Personalized Service", desc: "Layanan disesuaikan dengan kebutuhan pribadi." },
      { icon: HiClock, title: "24/7 Concierge", desc: "Dukungan penuh setiap saat, kapan pun." },
    ],
    teamTagline: "Meet The Experts",
    teamTitle: "Tim Di Balik KinKen",
    teamSubtitle: "Para profesional yang menghadirkan standar baru kost elite di Bali",
  } : {
    title: "About KinKen Kost Elite",
    subtitle: "Not just an ordinary kost. KinKen brings a new standard of premium living in Bali, where every detail is designed to support a sophisticated urban lifestyle.",
    superText: "Our Philosophy",
    storyTitle: "Redefining Luxury Living,",
    storyHighlight: "Crafting Experiences.",
    storyP1: "KinKen Kost Elite was born from a vision to present a living concept that exceeds expectations. In the heart of Renon, South Denpasar, we answer the needs of modern professionals and expatriates who want more than just a place to stay.",
    storyP2: "Every room at KinKen is designed like a mini private apartment with Balinese luxury touches. From 24/7 concierge service to 5-star hotel amenities, we create a",
    sanctuary: "sanctuary",
    storyP2End: "where productivity meets ultimate comfort.",
    occupancyLabel: "Occupancy Rate",
    satisfactionLabel: "Resident Satisfaction",
    values: [
      { icon: HiStar, title: "Elite Standard", desc: "Premium quality in every aspect of living." },
      { icon: HiUsers, title: "Curated Community", desc: "Selected community of professionals and expatriates." },
      { icon: HiHeart, title: "Personalized Service", desc: "Services tailored to personal needs." },
      { icon: HiClock, title: "24/7 Concierge", desc: "Full support anytime, whenever you need." },
    ],
    teamTagline: "Meet The Experts",
    teamTitle: "The Team Behind KinKen",
    teamSubtitle: "The professionals bringing a new standard of elite kost in Bali",
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="/images/about/hero-section.webp"
        superText={pageContent.superText}
      />

      {/* STORY SECTION */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2 grid grid-cols-2 gap-4">
              {STORY_IMAGES.map((img, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1, duration: 0.8 }}
                  className={`relative h-64 rounded-sm overflow-hidden ${
                    idx === 1 || idx === 2 ? "mt-8" : ""
                  }`}
                >
                  <Image
                    src={img}
                    alt="KinKen Kost Elite Interior & Facilities"
                    fill
                    className="object-cover hover:scale-110 transition-transform duration-700"
                  />
                </motion.div>
              ))}
            </div>

            <div className="w-full lg:w-1/2 space-y-8">
              <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal">
                {pageContent.storyTitle} <br />
                <span className="text-brand-gold">{pageContent.storyHighlight}</span>
              </h2>
              <div className="space-y-6 text-gray-500 font-light text-lg leading-relaxed">
                <p>{pageContent.storyP1}</p>
                <p>
                  {pageContent.storyP2}{" "}
                  <span className="text-gray-700 font-medium">{pageContent.sanctuary}</span>{" "}
                  {pageContent.storyP2End}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 pt-8 border-t border-gray-100">
                <div>
                  <h4 className="font-serif text-3xl text-brand-charcoal">95%</h4>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mt-1">
                    {pageContent.occupancyLabel}
                  </p>
                </div>
                <div>
                  <h4 className="font-serif text-3xl text-brand-charcoal">4.8/5</h4>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mt-1">
                    {pageContent.satisfactionLabel}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {pageContent.values.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-8 border border-gray-100 text-center hover:border-brand-gold transition-colors duration-300"
              >
                <div className="text-brand-gold mb-4 flex justify-center">
                  <item.icon size={32} />
                </div>
                <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm font-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="py-24 md:py-32 bg-white overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 text-center mb-16">
          <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4">
            {pageContent.teamTagline}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal">
            {pageContent.teamTitle}
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto mt-4">
            {pageContent.teamSubtitle}
          </p>
        </div>

        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <Swiper
            effect={"cards"}
            grabCursor={true}
            modules={[EffectCards, Autoplay]}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            className="w-[280px] md:w-[350px]"
          >
            {teamMembers.map((member) => (
              <SwiperSlide
                key={member.id}
                className="bg-white rounded-xl shadow-xl overflow-hidden"
              >
                <div className="relative h-[400px] w-full group cursor-pointer">
                  <div className="relative h-[300px] w-full overflow-hidden">
                    <Image
                      src={member.image}
                      alt={member.name}
                      fill
                      className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8 translate-y-4 group-hover:translate-y-0 transform">
                      <div className="h-[2px] w-12 bg-brand-gold mb-4"></div>
                      <p className="text-white text-sm font-light italic leading-relaxed">
                        "{member.bio}"
                      </p>
                    </div>
                  </div>

                  <div className="p-6 border-l-4 border-transparent group-hover:border-brand-gold transition-all duration-300 bg-white">
                    <h3 className="font-serif text-2xl text-brand-charcoal">
                      {member.name}
                    </h3>
                    <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-1 group-hover:text-brand-gold transition-colors">
                      {member.role}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      <Footer />
    </main>
  );
}
